import { Grid, Heading, Link, Provider, View, defaultTheme } from '@adobe/react-spectrum';
import { useBreakpoint } from '@react-spectrum/utils';
import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';

import { AboutImage } from './AboutImage';
import { getStaticData } from './utils';

// Styled Components
/**
 * main details container
 * @memberof AppDetails
 * @type {styled-component}
 */
const DetailApp = styled.div`
  padding-top: 20px;
  margin: 0 auto;
  max-width: 1280px;
  width: 90%;
`;

const Paragraph = styled.p`
  font-size: 18px;
`;

const About = () => {
  const { matchedBreakpoints } = useBreakpoint();
  const isMobileSized = !matchedBreakpoints.includes('L');
  const [images, setImages] = useState(null);

  useEffect(() => {
    let mounted = true;
    getStaticData('hostApps').then((response) => {
      if (mounted && response) {
        const imageArray = [];
        for (const value of response.values()) {
          if (value.featuredImage) {
            imageArray.push(value.featuredImage);
          }
        }
        const aemImage = imageArray.find((src) => src.includes('product--experience-manager'));
        const aepImage = imageArray.find((src) => src.includes('product--adobe-experience-platform'));
        const acImage = imageArray.find((src) => src.includes('product--advertising-cloud'));
        setImages({
          aemImage: {
            webp: aemImage.replace('.svg', '.webp'),
            svg: aemImage,
          },
          aepImage: {
            webp: aepImage.replace('.svg', '.webp'),
            svg: aepImage,
          },
          acImage: {
            webp: acImage.replace('.svg', '.webp'),
            svg: acImage,
          },
        });
      }
    });
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <Provider theme={defaultTheme} colorScheme={`light`}>
      <View data-testingid="about" minHeight="100vh">
        <Helmet>
          <title>{`About | Adobe Exchange`}</title>
          <meta name="robots" content="noodp, FOLLOW, INDEX" />
          <meta name="title" content={`About | Adobe Exchange`} />
          <meta property="og:title" content={`About | Adobe Exchange`} />
          <meta name="description" content={`About Adobe Exchange`} />
          <meta property="og:description" content={`About Adobe Exchange`} />
          <meta property="og:type" content="article" />
        </Helmet>
        <DetailApp>
          <View
            backgroundColor="static-white"
            borderRadius="regular"
            borderColor="gray-200"
            borderWidth="thin"
            padding="size-400"
            fontSize="18px"
          >
            <section id="about-exchange-marketplace">
              <Grid
                columns={isMobileSized ? '1fr' : '1fr 1fr'}
                alignItems="center"
                areas={isMobileSized ? ['content', 'image'] : ['content image']}
              >
                <View marginEnd={isMobileSized ? '0px' : '32px'}>
                  <Heading level={2}>About the Exchange Marketplace</Heading>

                  <Paragraph>
                    The Adobe Exchange Marketplace is a directory of integrations that Adobe customers can use to
                    enhance their Adobe Experience Cloud implementation. Each listing includes installation instructions
                    for you to learn about implementation for the integration, and the level of effort that would be
                    needed.
                  </Paragraph>

                  <Paragraph>
                    For more information on a specific integration, please reach out directly to the partner as they
                    will have the most up to date information on their integration. To report an issue with an
                    Experience Cloud listing, please submit a ticket{' '}
                    <Link>
                      <a
                        href="https://adobeexchangeec.zendesk.com/hc/en-us/requests/new"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        here
                      </a>
                    </Link>{' '}
                    and our Adobe Technology Partner Program support team will reach out to the partner and ask them to
                    correct the information.
                  </Paragraph>
                </View>
                <AboutImage imageSRC={images?.aemImage} />
              </Grid>
            </section>

            <section id="about-app-assurance-program">
              <Grid
                marginTop={isMobileSized ? '0px' : '128px'}
                columns={isMobileSized ? '1fr' : '1fr 1fr'}
                alignItems="center"
                areas={isMobileSized ? ['content', 'image'] : ['image content']}
              >
                <AboutImage imageSRC={images?.aepImage} />
                <View marginStart={isMobileSized ? '0px' : '32px'} gridArea="content">
                  <Heading level={2}>About App Assurance Program</Heading>
                  <Paragraph>
                    The App Assurance Program for the{' '}
                    <Link>
                      <a
                        href="https://partners.adobe.com/technologyprogram/experiencecloud.html"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Adobe Technology Partner Program
                      </a>
                    </Link>{' '}
                    is to further distinguish technology partner listings on the Exchange Marketplace. When applications
                    go through this Adobe process for a deep dive and those that completed the steps are of higher
                    quality, they are recognized with an assurance badge. The badge gives Adobe customers that needed
                    boost of confidence in the integration listed.
                  </Paragraph>
                </View>
              </Grid>
            </section>

            <section id="about-partner-level">
              <Grid
                marginTop={isMobileSized ? '0px' : '128px'}
                columns={isMobileSized ? '1fr' : '1fr 1fr'}
                alignItems="center"
                areas={isMobileSized ? ['content', 'image'] : ['content image']}
              >
                <View>
                  <Heading level={2}>About Partner Level</Heading>

                  <Paragraph>
                    The partner levels for the{' '}
                    <Link>
                      <a
                        href="https://partners.adobe.com/technologyprogram/experiencecloud.html"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Adobe Technology Partner Program
                      </a>
                    </Link>{' '}
                    are Community, Silver, Gold, and Platinum, with Platinum partners being the partners that have
                    invested the most in their partnership with Adobe.
                  </Paragraph>
                </View>
                <AboutImage imageSRC={images?.acImage} />
              </Grid>
            </section>
          </View>
        </DetailApp>
      </View>
    </Provider>
  );
};

export default About;
